import React, { useState } from "react";
import { BadgeCheck, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Activation toggle for the public "Auto Quote" badge contractors see on the marketplace.
export function AutoQuoteBadgeToggle({ className = "" }) {
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  if (!user || user.account_type === "contractor") return null;

  const enabled = !!user.auto_quote_enabled;

  const toggle = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { auto_quote_enabled: next });
      setUser(data);
      toast.success(next ? "Auto Quote badge activated" : "Auto Quote badge turned off");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not update the badge");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="auto-quote-badge-toggle"
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border px-4 py-3 transition-colors ${enabled ? "border-brand/40 bg-brand-soft" : "border-border bg-card"} ${className}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${enabled ? "bg-brand/15" : "bg-accent"}`}>
        <BadgeCheck className={`w-5 h-5 ${enabled ? "text-brand" : "text-muted-foreground"}`} />
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          Auto Quote badge
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          This confirms your auto quote for contractors information.
        </p>
      </div>
      <Switch
        data-testid="auto-quote-badge-switch"
        checked={enabled}
        disabled={saving}
        onCheckedChange={toggle}
      />
    </div>
  );
}
